import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { lmsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, GraduationCap, Search, Award, PlayCircle, CheckCircle2 } from 'lucide-react';

export default function CourseCatalogPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    Promise.all([lmsApi.listCourses(), lmsApi.myEnrollments(), lmsApi.myCertificates()])
      .then(([c, e, k]) => { setCourses(c.data); setEnrollments(e.data); setCerts(k.data); })
      .finally(() => setLoading(false));
  }, []);

  const enrollMap = Object.fromEntries(enrollments.map(e => [e.course_id, e]));
  const filtered = courses.filter(c =>
    !q.trim() || c.title.toLowerCase().includes(q.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(q.toLowerCase())
  );
  const byCategory = filtered.reduce((acc, c) => {
    const k = c.category || 'General';
    (acc[k] = acc[k] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="course-catalog-page">
      <div>
        <h1 className="text-3xl font-bold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <GraduationCap className="w-7 h-7 text-[#0E7490]" />Learn
        </h1>
        <p className="text-[#64748B] mt-1">Training courses, onboarding modules, and certifications.</p>
      </div>

      {certs.length > 0 && (
        <Card className="careiq-card border-l-4 border-l-[#CA8A04]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#92400E] font-medium mb-2"><Award className="w-4 h-4" />Your certificates ({certs.length})</div>
            <div className="flex flex-wrap gap-2">
              {certs.map(c => (
                <Badge key={c.certificate_number} className="bg-[#FEF3C7] text-[#92400E] border border-[#CA8A04]/30" data-testid={`cert-${c.certificate_number}`}>
                  {c.course_title} · {c.certificate_number}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses…" className="pl-9" data-testid="course-search-input" />
      </div>

      {loading ? <Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /> : (
        Object.keys(byCategory).length === 0 ? (
          <Card className="careiq-card"><CardContent className="p-10 text-center text-[#64748B]">No courses available yet.</CardContent></Card>
        ) : (
          Object.entries(byCategory).map(([cat, list]) => (
            <div key={cat} className="space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0E7490]">{cat}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map(c => {
                  const e = enrollMap[c.id];
                  const total = c.lesson_count || 0;
                  const done = e?.lesson_progress_count || 0;
                  const pct = total ? Math.round((done / total) * 100) : 0;
                  const completed = e?.completed_at;
                  return (
                    <Card key={c.id} className="careiq-card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/learn/courses/${c.id}`)} data-testid={`learn-course-${c.id}`}>
                      {c.thumbnail_url ? (
                        <div className="w-full h-40 bg-[#F1F5F9] overflow-hidden">
                          <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-full h-40 bg-gradient-to-br from-[#0E7490]/15 to-[#0A2342]/15 flex items-center justify-center">
                          <PlayCircle className="w-14 h-14 text-[#0E7490]/40" />
                        </div>
                      )}
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-[#64748B]">{total} lesson{total !== 1 ? 's' : ''}</div>
                          {completed && <Badge className="bg-[#DCFCE7] text-[#166534]"><CheckCircle2 className="w-3 h-3 mr-1" />Completed</Badge>}
                        </div>
                        <h3 className="font-semibold text-[#0A2342] line-clamp-2">{c.title}</h3>
                        <p className="text-xs text-[#64748B] line-clamp-2">{c.description}</p>
                        {e && !completed && total > 0 && (
                          <div className="pt-2">
                            <div className="h-1.5 rounded-full bg-[#E2E8F0] overflow-hidden">
                              <div className="h-full bg-[#0E7490]" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-[10px] text-[#64748B] mt-1">{done}/{total} lessons · {pct}%</div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))
        )
      )}
    </div>
  );
}
